import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  Building2,
  HandCoins,
  Users2,
  TrendingUp,
  ShieldCheck,
  Briefcase,
  Scale,
  FileCheck2,
  Gem,
  Crown,
  Trophy,
  Sparkles,
  PiggyBank,
  Repeat,
  Banknote,
  Medal,
  Star,
  Gift,
  Network,
  Store,
  GraduationCap,
  IndianRupee,
  Phone,
  MessageCircle,
  ArrowRight,
  MapPin,
  CheckCircle2,
  XCircle,
  Quote,
  Clock,
  FileText,
  Headphones,
  Megaphone,
  Lock,
  Zap,
  UserCheck,
  Infinity as InfinityIcon,
  Mail,
} from "lucide-react";
import { PolicyHeader } from "@/components/PolicyHeader";

/* ─── DATA ────────────────────────────────────────────────────────────── */

const PILLARS = [
  { icon: ShieldCheck, title: "Your Trust", sub: "Our Priority" },
  { icon: HandCoins, title: "Your Service", sub: "Our Commitment" },
  { icon: Trophy, title: "Your Success", sub: "Our Goal" },
];

const STRUCTURE = [
  {
    step: "1",
    title: "Branch",
    color: "from-emerald-500/30 to-emerald-600/10 border-emerald-400/40",
    icon: Building2,
    bullets: ["Company Branches", "Local Presence", "Provide Support & Resources"],
  },
  {
    step: "2",
    title: "Agency Partner",
    color: "from-amber-500/30 to-amber-600/10 border-amber-400/40",
    icon: Briefcase,
    bullets: [
      "Fixed Salary + Commission",
      "Team Override Income",
      "Leadership Bonus",
      "Business Development",
    ],
  },
  {
    step: "3",
    title: "DSA",
    sub: "Direct Selling Agent",
    color: "from-violet-500/30 to-violet-600/10 border-violet-400/40",
    icon: Network,
    bullets: ["Commission Based", "Customer Acquisition", "Loan Sourcing", "Earn Incentives"],
  },
  {
    step: "4",
    title: "Advisor",
    color: "from-rose-500/30 to-rose-600/10 border-rose-400/40",
    icon: Users2,
    bullets: [
      "1.50% Commission",
      "Customer Guidance",
      "Relationship Building",
      "Part-Time / Full-Time",
    ],
  },
];

const PREMIUM_SERVICES = [
  {
    icon: HandCoins,
    title: "Business Loans",
    desc: "Fast & flexible business financing for every stage of growth.",
  },
  {
    icon: TrendingUp,
    title: "Project Finance",
    desc: "Customised funding solutions for large infrastructure and corporate projects.",
  },
  {
    icon: PiggyBank,
    title: "Investments",
    desc: "Mutual funds, equity, bonds and more — diversified portfolios.",
  },
  {
    icon: ShieldCheck,
    title: "Insurance",
    desc: "Life · Health · Motor · General — comprehensive cover for what matters.",
  },
  {
    icon: Briefcase,
    title: "Advisory",
    desc: "Financial planning & wealth advisory tailored to your goals.",
  },
  {
    icon: Building2,
    title: "Corporate Solutions",
    desc: "End-to-end business solutions for SMEs, MSMEs and corporates.",
  },
  {
    icon: Scale,
    title: "Risk & Due Diligence",
    desc: "Identify risks, evaluate opportunities and secure growth confidently.",
  },
  {
    icon: FileCheck2,
    title: "Regulatory Compliance",
    desc: "Complete compliance support across statutory, tax and corporate filings.",
  },
  {
    icon: Gem,
    title: "Legal Advisory",
    desc: "Expert legal guidance and watertight documentation.",
  },
];

const WHY_CHOOSE = [
  "Experienced Professionals",
  "Client-Centric Approach",
  "Ethical & Transparent",
  "Personalized Strategies",
  "Long-Term Partnership",
  "Commitment to Excellence",
];

const INCOME_CATEGORIES = [
  { icon: HandCoins, label: "Direct Commission" },
  { icon: Network, label: "Override Commission" },
  { icon: Users2, label: "Team Commission" },
  { icon: TrendingUp, label: "Performance Bonus" },
  { icon: Crown, label: "Leadership Bonus" },
  { icon: Gift, label: "Referral Bonus" },
  { icon: Building2, label: "Branch Incentive" },
  { icon: Store, label: "Franchise Incentive" },
  { icon: PiggyBank, label: "Profit Sharing" },
  { icon: Award, label: "Annual Recognition Awards" },
];

const ADVISOR_PERKS = [
  "Customer Lead Generation",
  "Loan Product Guidance",
  "Document Collection Support",
  "Customer Relationship Management",
  "Part-Time / Full-Time Opportunity",
  "No Fixed Income Requirement",
  "Performance Based Growth",
];

const INCOME_STRUCTURE = [
  { level: "Advisor", commission: "1.50%" },
  { level: "DSA", commission: "Up to 2.00%" },
  { level: "Agency Partner", commission: "Up to 2.50% – 5.00%" },
  { level: "Business Partner", commission: "Profit Sharing + Incentives" },
];



const WHY_PARTNER = [
  { icon: TrendingUp, label: "High Earning Potential" },
  { icon: HandCoins, label: "Attractive Commission Structure" },
  { icon: Users2, label: "Team Override Income" },
  { icon: Crown, label: "Leadership Bonus" },
  { icon: Sparkles, label: "Performance Incentives" },
  { icon: Gift, label: "Referral Bonus" },
  { icon: Building2, label: "Branch Incentive" },
  { icon: Store, label: "Franchise Opportunity" },
  { icon: PiggyBank, label: "Profit Sharing" },
  { icon: Award, label: "Annual Recognition Awards" },
];

const SLABS = [
  {
    slab: "SLAB-1",
    level: "Starter Level",
    range: "₹5 Lakh – ₹10 Lakh",
    commission: "2.00%",
    salary: "₹10,000/-",
    benefits: ["Joining Support", "Training & Guidance"],
  },
  {
    slab: "SLAB-2",
    level: "Executive Level",
    range: "₹10 Lakh – ₹1 Crore",
    commission: "2.50%",
    salary: "₹20,000/-",
    benefits: [
      "Business Development Support",
      "Lead Generation Assistance",
      "Priority Lead Support",
    ],
  },
  {
    slab: "SLAB-3",
    level: "Senior Executive",
    range: "₹1 Crore – ₹5 Crore",
    commission: "3.00%",
    salary: "₹30,000/-",
    benefits: [
      "Team Building Opportunity",
      "Performance Recognition",
      "Senior DSA Status",
    ],
  },
  {
    slab: "SLAB-4",
    level: "Regional Leader",
    range: "₹5 Crore – ₹25 Crore",
    commission: "4.00%",
    salary: "₹50,000/-",
    benefits: [
      "Special Business Incentives",
      "Regional Leadership Benefits",
      "Corporate Recognition",
    ],
  },
  {
    slab: "SLAB-5",
    level: "National Leader",
    range: "₹25 Crore – ₹50 Crore",
    commission: "5.00%",
    salary: "₹80,000/-",
    benefits: [
      "National Leadership",
      "Exclusive Rewards & Incentives",
      "Annual Performance Awards",
      "Business Expansion Support",
    ],
  },
];

const ELIGIBILITY = [
  { label: "Age", value: "18 to 60 Years" },
  { label: "Education", value: "Minimum 10th Pass" },
  { label: "Experience", value: "Freshers & Experienced Both Eligible" },
  { label: "Location", value: "Gujarat & All India" },
];

const FINANCIAL_SERVICES = [
  "Personal Loan",
  "Business Loan",
  "Project Finance",
  "Working Capital Finance",
  "Agriculture Loan",
  "Plot Purchase Loan",
  "Plot Loan",
  "Loan Against Open Plot",
  "Gold Loan",
  "Gold Loan for Buy",
  "Mortgage Loan",
  "Loan Against Property",
  "Loan Against Securities",
  "Loan Against Security Deposit",
];

const SUPPORT_BENEFITS = [
  { icon: IndianRupee, label: "No Fixed Income Required" },
  { icon: Star, label: "18+ Years Eligibility" },
  { icon: FileCheck2, label: "Easy Documentation" },
  { icon: Banknote, label: "Fast Payout" },
  { icon: GraduationCap, label: "Training & Guidance Support" },
  { icon: Repeat, label: "Digital Tools & CRM Access" },
];


// ─── Full corporate hierarchy from the marketing poster ──────────────
const HIERARCHY_6 = [
  {
    n: "1",
    title: "Head Office",
    icon: Building2,
    accent: "border-slate-400/40 from-slate-400/15",
    role: ["Central Management", "Strategic Planning", "Policy Making", "Support & Monitoring"],
    benefits: ["Leadership Income", "National Incentives", "Equity Participation"],
  },
  {
    n: "2",
    title: "Branch",
    icon: Store,
    accent: "border-emerald-500/40 from-emerald-500/15",
    role: ["Local Operations", "Team Management", "Business Development"],
    benefits: ["Branch Performance Bonus", "Team Growth Incentive", "High Revenue Sharing"],
  },
  {
    n: "3",
    title: "Sub Branch",
    icon: Building2,
    accent: "border-sky-500/40 from-sky-500/15",
    role: ["Customer Service", "Loan Processing", "Local Support"],
    benefits: ["Operational Incentive", "Customer Portfolio Bonus", "Growth Rewards"],
  },
  {
    n: "4",
    title: "Associate Partner",
    icon: Users2,
    accent: "border-violet-500/40 from-violet-500/15",
    role: ["Business Partner", "Network Expansion", "Lead Generation"],
    benefits: ["Fixed Salary + Commission", "Leadership Bonus", "Business Development Bonus"],
  },
  {
    n: "5",
    title: "DSA",
    sub: "Direct Selling Agent",
    icon: Network,
    accent: "border-amber-500/40 from-amber-500/15",
    role: ["Direct Selling Agent", "Customer Acquisition"],
    benefits: ["High Commission", "Incentive on Disbursement", "Performance Bonus"],
  },
  {
    n: "6",
    title: "Advisor",
    icon: Briefcase,
    accent: "border-rose-500/40 from-rose-500/15",
    role: ["Financial Advisor", "Client Guidance", "Product Advisory"],
    benefits: ["Advisory Commission", "Renewal Income", "Long Term Incentives"],
  },
];

const REGISTRATION_FEES = [
  { level: "Advisor",            fee: "₹2,500",       gst: "₹450",      total: "₹2,950",      perks: "Advisory Rights · Training · Product Access · Income Opportunities" },
  { level: "DSA",                fee: "₹11,000",      gst: "₹1,980",    total: "₹12,980",     perks: "DSA Code · Lead Access · Commission · Incentives · Support" },
  { level: "Associate Partner",  fee: "₹15,00,000",   gst: "₹2,70,000", total: "₹17,70,000",  perks: "Leadership Role · Higher Income · Team Building · Profit Sharing" },
  { level: "Agency Partner (Branch Level)", fee: "₹35,00,000", gst: "₹6,30,000", total: "₹41,30,000", perks: "Branch Operations · High Income · National Level Benefits" },
];

const COMMISSION_BY_LEVEL = [
  { lvl: "1", role: "Head Office",        source: "Overall Profit Sharing · National Performance",   base: "Up to 2.00% of Total Business + Leadership Bonus" },
  { lvl: "2", role: "Branch",             source: "Branch Business Volume · Team Performance",         base: "Up to 1.50% of Disbursement + Incentives" },
  { lvl: "3", role: "Sub Branch",         source: "Loan Disbursement · Customer Base",                 base: "Up to 1.00% of Disbursement + Performance Bonus" },
  { lvl: "4", role: "Associate Partner",  source: "Team Building · Business Development",              base: "Up to 0.75% of Disbursement + High Incentives" },
  { lvl: "5", role: "DSA",                source: "Loan Sourcing · Customer Acquisition",              base: "Up to 0.50% of Disbursement + High Incentives" },
  { lvl: "6", role: "Advisor",            source: "Advisory · Cross-Selling · Portfolio Management",    base: "Up to 0.25% of Disbursement + Advisory Fees" },
];

const PERF_INCENTIVES = [
  { target: "₹10 Lakh – ₹25 Lakh", type: "Performance Bonus",   amt: "₹5,000 – ₹10,000" },
  { target: "₹25 Lakh – ₹50 Lakh", type: "Performance Bonus",   amt: "₹12,500 – ₹25,000" },
  { target: "₹50 Lakh – ₹1 Crore", type: "Performance Bonus",   amt: "₹50,000 – ₹1,50,000" },
  { target: "Above ₹1 Crore",      type: "Top Performer Bonus", amt: "₹2,00,000+" },
  { target: "Team Building Bonus", type: "For Building a Successful Team", amt: "Up to ₹20,000" },
  { target: "Leadership Bonus",    type: "For Upgrading Your Team",        amt: "Extra % on Overrides" },
  { target: "Annual Recognition",  type: "Top Achievers Award",            amt: "Foreign Trip / Cash / Gifts" },
];

const EARNING_POTENTIAL = [
  { level: "Advisor",            biz: "₹10 Lakh",  total: "₹5,000 – ₹10,000" },
  { level: "DSA",                biz: "₹25 Lakh",  total: "₹12,500 – ₹25,000" },
  { level: "Associate Partner",  biz: "₹1 Crore",  total: "₹50,000 – ₹1,50,000" },
  { level: "Sub Branch",         biz: "₹2 Crore",  total: "₹1,00,000 – ₹3,00,000" },
  { level: "Branch",             biz: "₹5 Crore",  total: "₹2,50,000 – ₹7,50,000" },
  { level: "Head Office",        biz: "₹25+ Crore", total: "₹15,00,000+" },
];

const DSA_BENEFITS = [
  "High Commission on Every Disbursement",
  "Timely Payout & Transparent Process",
  "No Investment – Start Your Journey FREE",
  "Training & Guidance by Experts",
  "Marketing & Lead Support",
  "Rewards, Recognition & Foreign Trips",
  "Unlimited Income Opportunity",
];



const WHO_CAN_JOIN = [
  "Businessmen",
  "Shop Owners",
  "Real Estate Agents",
  "Insurance Agents",
  "Finance Professionals",
  "Students (Part Time)",
  "Any Individual (18+ Years)",
];

const WHY_JOIN = [
  { en: "No Investment", gu: "કોઈ રોકાણ નથી" },
  { en: "No Fixed Income", gu: "કોઈ ફિક્સ આવક નથી" },
  { en: "High Commission", gu: "વધુ કમિશન" },
  { en: "Unlimited Income", gu: "આવકની કોઈ મર્યાદા નથી" },
  { en: "Part Time / Full Time", gu: "તમારી પસંદગી" },
  { en: "Job & Business Opportunity", gu: "નોકરી અને બિઝનેસ" },
  { en: "Financial Freedom", gu: "આર્થિક સ્વતંત્રતા તરફ મહત્વપૂર્ણ પગલું" },
  { en: "Your effort = Your earning", gu: "તમારી મહેનત = તમારી કમાણી" },
];

const DSA_PROCESS = [
  { step: "1", title: "Register with Sahyog Finance", icon: UserCheck },
  { step: "2", title: "Get Training & Guidance", icon: GraduationCap },
  { step: "3", title: "Share Documents", icon: FileText },
  { step: "4", title: "Verification", icon: ShieldCheck },
  { step: "5", title: "Loan Disbursement", icon: Banknote },
  { step: "6", title: "Earn Attractive Commissions", icon: HandCoins },
];

const COMPARISON = [
  {
    them: "CIBIL Score 700+ Required",
    themGu: "CIBIL Score 700+ જરૂરી",
    us: "Loan facility even without CIBIL Score",
    usGu: "CIBIL Score વગર પણ લોન સુવિધા",
  },
  {
    them: "Fixed business income (₹35,000+) needed",
    themGu: "વ્યવસાય માટે લોન (₹35,000+) જરૂરી",
    us: "Fixed income is not required",
    usGu: "ફિક્સ ઇન્કમ આવશ્યક નથી",
  },
  {
    them: "Existing loan / EMI / obligation disclosure",
    themGu: "હાલની લોન / EMI / Obligation ની માહિતી",
    us: "Cases considered even with existing obligations",
    usGu: "હાલની લોન અથવા Obligation હોવા છતાં કેસ પર વિચારણા",
  },
];

const DOCUMENTS = [
  { en: "Aadhaar Card", gu: "આધાર કાર્ડ" },
  { en: "PAN Card", gu: "PAN કાર્ડ" },
  { en: "Bank Statement (last 12 months)", gu: "બેંક સ્ટેટમેન્ટ (છેલ્લા 12 મહિના)" },
  { en: "Passport-size Photo", gu: "પાસપોર્ટ સાઈઝ ફોટો" },
  { en: "Address Proof", gu: "સરનામાનું પુરાવો" },
  { en: "Income / Business Proof (if any)", gu: "આવક અથવા વ્યવસાય આવકની માહિતી" },
];

const BUSINESS_VERTICALS = [
  {
    title: "Sahyog Finance",
    tag: "Loan & Financial Solutions",
    desc: "Retail & Corporate Loans across India.",
    icon: Building2,
    accent: "border-emerald-500/40 from-emerald-500/15",
  },
  {
    title: "Sahyog Project Finance",
    tag: "Investment · Retail",
    desc: "Project Finance, Investment & Retail Funding Solutions.",
    icon: TrendingUp,
    accent: "border-amber-500/40 from-amber-500/15",
  },
  {
    title: "Noble Infinity",
    tag: "Asset Management Pvt. Ltd.",
    desc: "Wealth Management · Asset Advisory · Investment Management Solutions.",
    icon: InfinityIcon,
    accent: "border-violet-500/40 from-violet-500/15",
  },
];

const TRUST_PILLARS = [
  { icon: ShieldCheck, label: "Trusted & Reliable" },
  { icon: Lock, label: "Secure & Transparent" },
  { icon: Zap, label: "Fast Processing" },
  { icon: FileCheck2, label: "Minimum Documentation" },
  { icon: Star, label: "Expert Guidance" },
  { icon: Headphones, label: "End-to-End Support" },
];

const PARTNERSHIP_PERKS = [
  { icon: HandCoins, label: "Attractive Commission Structure" },
  { icon: TrendingUp, label: "Unlimited Income Opportunity" },
  { icon: Sparkles, label: "Fast Career Growth" },
  { icon: Clock, label: "Flexible Working Hours" },
  { icon: IndianRupee, label: "No Investment Required" },
  { icon: GraduationCap, label: "Full Training Support" },
  { icon: Megaphone, label: "Marketing Material Support" },
  { icon: UserCheck, label: "Dedicated Relationship Manager" },
];

const REGISTRATIONS = [
  { label: "GST Registered", value: "24AYFFPG5674L2Z4", icon: FileCheck2 },
  { label: "UDYAM (MSME)", value: "GJ-24-0178730", icon: ShieldCheck },
  { label: "MCA Registered", value: "U66190GJ2025PTC171248", icon: Building2 },
  { label: "PAN Verified", value: "AALCN1559R", icon: FileText },
  { label: "TAN Verified", value: "AHMN15110E", icon: FileText },
];

const MOTTO_LINES = [
  { en: "Growth begins with a Decision", gu: "વૃદ્ધિની શરૂઆત નિર્ણયથી" },
  { en: "Success begins with Action", gu: "સફળતા ક્રિયા થી શરૂ થાય છે" },
  { en: "Wealth begins with Discipline", gu: "સંપત્તિ શિસ્ત થી શરૂ થાય છે" },
];


/* ─── PAGE ────────────────────────────────────────────────────────────── */

export default function Opportunity() {
  return (
    <div
      data-testid="opportunity-page"
      className="min-h-screen bg-[#04102B] text-white relative overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-500/8 blur-3xl" />

      <PolicyHeader testId="opp-header" />

      <main id="main-content" tabIndex={-1} className="relative max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-20 space-y-24">
        {/* ── HERO ─────────────────────────────────────────────────── */}
        <section data-testid="opp-hero" className="text-center">
          <span className="inline-block text-[11px] tracking-[0.32em] uppercase text-[#D4AF37]">
            · Partnership Opportunity · ESTD. 2024 ·
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight">
            Join A Trusted Brand.
            <br />
            <span className="text-[#D4AF37]">Build Your Future. Earn Without Limits!</span>
          </h1>
          <p className="mt-7 max-w-3xl mx-auto text-white/75 text-base sm:text-lg leading-relaxed">
            Building Wealth · Creating Value · Securing Your Future. Become an Advisor, DSA,
            Agency Partner or Business Partner with Sahyog Finance &amp; Noble Infinity —
            India&apos;s most inclusive financial growth platform.
          </p>
          <p className="mt-3 font-display text-[#D4AF37] text-xl tracking-tight">
            સહયોગ આપણો... વિકાસ આપણો...
          </p>

          {/* ─── JOINING FEES — POSTER STYLE ─────────────────────── */}
          <div
            data-testid="opp-fees-poster"
            className="mt-12 rounded-3xl border-2 border-[#D4AF37]/50 bg-gradient-to-br from-[#0A1F44] via-[#04102B] to-[#0A1F44] p-7 md:p-10"
          >
            <div className="text-center mb-7">
              <div className="inline-block text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1.5">
                · Registration / Joining Fees · One-Time ·
              </div>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl text-white leading-tight">
                Choose Your Role · Pay Once · Earn for Life
              </h2>
              <p className="mt-2 text-xs text-white/55">
                All fees one-time &amp; non-refundable · GST @ 18% applicable
              </p>
            </div>
            <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
              {[
                { role: "Advisor",            slug: "advisor",     base: "₹2,500",       gst: "₹450",        total: "₹2,950",        tone: "from-rose-500/15 border-rose-400/40" },
                { role: "DSA",                slug: "dsa",         base: "₹11,000",      gst: "₹1,980",      total: "₹12,980",       tone: "from-amber-500/15 border-amber-400/40" },
                { role: "Associate Partner",  slug: "associate",   base: "₹15,00,000",   gst: "₹2,70,000",   total: "₹17,70,000",    tone: "from-violet-500/15 border-violet-400/40" },
                { role: "Agency Partner",     slug: "agency",      base: "₹35,00,000",   gst: "₹6,30,000",   total: "₹41,30,000",    tone: "from-emerald-500/15 border-emerald-400/40" },
                { role: "Sub Branch",         slug: "sub_branch",  base: "₹70,00,000",   gst: "₹12,60,000",  total: "₹82,60,000",    tone: "from-sky-500/15 border-sky-400/40" },
                { role: "Branch",             slug: "branch",      base: "₹1,20,00,000", gst: "₹21,60,000",  total: "₹1,41,60,000",  tone: "from-fuchsia-500/15 border-fuchsia-400/40" },
              ].map((f) => (
                <div
                  key={f.role}
                  data-testid={`fee-card-${f.role.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-2xl border-2 ${f.tone} to-transparent bg-gradient-to-br p-4 md:p-5 text-left flex flex-col`}
                >
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">
                    {f.role}
                  </div>
                  <div className="mt-3 space-y-1 text-[11px] text-white/70">
                    <div className="flex justify-between">
                      <span>One-time</span>
                      <span className="text-white">{f.base}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>+ 18% GST</span>
                      <span className="text-white">{f.gst}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/15">
                    <div className="text-[9px] tracking-[0.22em] uppercase text-emerald-300">Total</div>
                    <div className="font-display text-lg xl:text-xl 2xl:text-2xl text-[#F0D788] leading-none mt-1 wrap-anywhere">
                      {f.total}
                    </div>
                  </div>
                  <Link
                    to={`/apply/${f.slug}`}
                    data-testid={`fee-apply-${f.slug}`}
                    aria-label={`Register as ${f.role}`}
                    className="mt-4 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full btn-gold text-xs font-semibold min-h-[44px]"
                  >
                    Become {f.role} <ArrowRight size={13} aria-hidden="true" />
                  </Link>
                </div>
              ))}
            </div>
            <div className="mt-5 text-center text-[11px] text-white/45">
              Joining fee includes code activation, training, marketing material &amp; CRM access.
            </div>
          </div>



          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link
              data-testid="opp-cta-advisor"
              to="/apply/advisor"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold"
            >
              Become an Advisor <ArrowRight size={16} />
            </Link>
            <Link
              data-testid="opp-cta-dsa"
              to="/apply/dsa"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-gold font-semibold"
            >
              Become a DSA <ArrowRight size={16} />
            </Link>
            <a
              data-testid="opp-cta-whatsapp"
              href="https://wa.me/917016975324"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30"
            >
              <MessageCircle size={16} /> WhatsApp Us
            </a>
          </div>

          {/* WhatsApp role picker — quick registration handoff */}
          <WhatsAppRolePicker />

          {/* Pillars */}
          <div className="mt-14 grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {PILLARS.map(({ icon: Icon, title, sub }) => (
              <div
                key={title}
                data-testid={`opp-pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl border border-[#D4AF37]/30 bg-white/5 p-5 backdrop-blur-sm"
              >
                <Icon className="text-[#D4AF37] mx-auto" size={28} />
                <div className="mt-3 font-display text-lg">{title}</div>
                <div className="text-[#D4AF37] text-xs tracking-[0.22em] uppercase mt-1">
                  {sub}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── INSTITUTIONAL STRUCTURE ─────────────────────────────── */}
        <section data-testid="opp-structure">
          <SectionHead
            eyebrow="Our Institutional Structure"
            title="A Career Ladder Built for You"
            sub="Start as an Advisor (1.50% commission) and grow into a Business Partner with unlimited income potential."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STRUCTURE.map((s) => (
              <div
                key={s.step}
                data-testid={`opp-structure-${s.step}`}
                className={`relative rounded-2xl border bg-gradient-to-br ${s.color} p-6`}
              >
                <div className="flex items-start justify-between">
                  <div className="font-display text-5xl text-white/30">{s.step}</div>
                  <s.icon className="text-[#F0D788]" size={28} />
                </div>
                <div className="mt-4 font-display text-2xl">{s.title}</div>
                {s.sub && (
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mt-1">
                    {s.sub}
                  </div>
                )}
                <ul className="mt-4 space-y-2 text-sm text-white/85">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-[#D4AF37]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm uppercase tracking-[0.2em] text-white/70">
            <span>Advisor</span>
            <ArrowRight size={14} className="text-[#D4AF37]" />
            <span>DSA</span>
            <ArrowRight size={14} className="text-[#D4AF37]" />
            <span>Agency Partner</span>
            <ArrowRight size={14} className="text-[#D4AF37]" />
            <span className="text-[#F0D788]">Business Partner</span>
          </div>
        </section>

        {/* ── 6-LEVEL HIERARCHY (Head Office → Advisor) ───────────── */}
        <section data-testid="opp-hierarchy-6">
          <SectionHead
            eyebrow="Our Hierarchy & Growth Structure"
            title="Six Levels · Each One a Bigger Opportunity"
            sub="From a freshly-joined Advisor to Head Office leadership — every promotion unlocks new income streams."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HIERARCHY_6.map((h) => (
              <div
                key={h.n}
                data-testid={`hier-${h.n}`}
                className={`relative rounded-2xl border bg-gradient-to-br ${h.accent} to-transparent p-6`}
              >
                <div className="flex items-start justify-between">
                  <div className="font-display text-5xl text-white/20">{h.n}</div>
                  <h.icon className="text-[#F0D788]" size={28} />
                </div>
                <div className="mt-4 font-display text-2xl">{h.title}</div>
                {h.sub && (
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mt-1">
                    {h.sub}
                  </div>
                )}
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-white/55">Role</div>
                    <ul className="mt-1 space-y-1 text-xs text-white/85">
                      {h.role.map((r) => (
                        <li key={r} className="flex items-start gap-1.5">
                          <span className="mt-1 w-1 h-1 rounded-full bg-[#D4AF37] shrink-0" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-emerald-300">Benefits</div>
                    <ul className="mt-1 space-y-1 text-xs text-white/85">
                      {h.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-1.5">
                          <CheckCircle2 size={11} className="text-emerald-300 mt-0.5 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center font-display text-lg text-[#F0D788]">
            Each Level — Bigger Opportunity, Higher Income!
          </div>

          {/* Three-line summary strip */}
          <div
            data-testid="opp-hierarchy-summary"
            className="mt-6 grid gap-3 md:grid-cols-3"
          >
            <div className="rounded-2xl border border-[#D4AF37]/30 bg-white/[0.03] px-5 py-4">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">01</div>
              <div className="mt-1 text-white text-sm md:text-base leading-snug">
                6 levels — each one a bigger opportunity.
              </div>
            </div>
            <div className="rounded-2xl border border-[#D4AF37]/30 bg-white/[0.03] px-5 py-4">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">02</div>
              <div className="mt-1 text-white text-sm md:text-base leading-snug">
                One-time joining fees — transparent &amp; non-refundable.
              </div>
            </div>
            <div className="rounded-2xl border border-[#D4AF37]/30 bg-white/[0.03] px-5 py-4">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">03</div>
              <div className="mt-1 text-white text-sm md:text-base leading-snug">
                6 income tiers — from 0.25% advisory to 2% Head Office monthly overrides.
              </div>
            </div>
          </div>
        </section>

        {/* ── REGISTRATION / INVESTMENT FEES ──────────────────────── */}
        <section data-testid="opp-reg-fees">
          <SectionHead
            eyebrow="Registration / Investment Fees"
            title="One-Time Joining Fees · Transparent & Non-Refundable"
            sub="GST @ 18% applicable. Fees include training, code activation, marketing material and ongoing support."
          />
          <div className="overflow-x-auto rounded-2xl border border-[#D4AF37]/30">
            <table className="min-w-full text-sm">
              <thead className="bg-[#0A1F44] text-[11px] tracking-[0.2em] uppercase text-white/80">
                <tr>
                  <th className="px-4 py-3 text-left">Level</th>
                  <th className="px-4 py-3 text-left">One-Time Fee</th>
                  <th className="px-4 py-3 text-left">GST @ 18%</th>
                  <th className="px-4 py-3 text-left">Total</th>
                  <th className="px-4 py-3 text-left">Benefits Included</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {REGISTRATION_FEES.map((r, i) => (
                  <tr
                    key={r.level}
                    data-testid={`opp-fee-${i}`}
                    className={i % 2 === 0 ? "bg-white/[0.02]" : ""}
                  >
                    <td className="px-4 py-4 align-top font-medium">{r.level}</td>
                    <td className="px-4 py-4 align-top text-white/85 whitespace-nowrap">{r.fee}</td>
                    <td className="px-4 py-4 align-top text-white/65 whitespace-nowrap">{r.gst}</td>
                    <td className="px-4 py-4 align-top">
                      <span className="font-display text-lg text-[#F0D788] whitespace-nowrap">{r.total}</span>
                    </td>
                    <td className="px-4 py-4 align-top text-white/75 text-xs leading-relaxed">{r.perks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[11px] text-white/55">
            Note: Fees are one-time and non-refundable. GST as applicable per Indian tax regulations.
          </div>
        </section>

        {/* ── COMMISSION STRUCTURE BY LEVEL ───────────────────────── */}
        <section data-testid="opp-commission-table">
          <SectionHead
            eyebrow="Commission Structure (by Seniority Level)"
            title="Six Income Tiers · From 0.25% Advisory to 2.00% Head Office"
          />
          <div className="overflow-x-auto rounded-2xl border border-[#D4AF37]/30">
            <table className="min-w-full text-sm">
              <thead className="bg-[#0A1F44] text-[11px] tracking-[0.2em] uppercase text-white/80">
                <tr>
                  <th className="px-4 py-3 text-left">Lv</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Main Income Source</th>
                  <th className="px-4 py-3 text-left">Base Commission / Benefit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {COMMISSION_BY_LEVEL.map((r, i) => (
                  <tr key={r.role} data-testid={`opp-comm-${i}`} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
                    <td className="px-4 py-4 align-top font-display text-[#F0D788] text-lg">{r.lvl}</td>
                    <td className="px-4 py-4 align-top font-medium">{r.role}</td>
                    <td className="px-4 py-4 align-top text-white/75 text-xs">{r.source}</td>
                    <td className="px-4 py-4 align-top text-[#D4AF37] text-sm">{r.base}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── PERFORMANCE INCENTIVES (Monthly) ────────────────────── */}
        <section data-testid="opp-perf-incentives">
          <SectionHead
            eyebrow="Performance Incentives (Monthly)"
            title="Monthly Targets · Cash, Trips & Recognition"
          />
          <div className="overflow-x-auto rounded-2xl border border-[#D4AF37]/30">
            <table className="min-w-full text-sm">
              <thead className="bg-[#0A1F44] text-[11px] tracking-[0.2em] uppercase text-white/80">
                <tr>
                  <th className="px-4 py-3 text-left">Target Achieved (Monthly)</th>
                  <th className="px-4 py-3 text-left">Incentive Type</th>
                  <th className="px-4 py-3 text-left">Benefit Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {PERF_INCENTIVES.map((r, i) => (
                  <tr key={r.target} data-testid={`opp-perf-${i}`} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
                    <td className="px-4 py-4 align-top">{r.target}</td>
                    <td className="px-4 py-4 align-top text-white/75">{r.type}</td>
                    <td className="px-4 py-4 align-top text-[#F0D788] font-display text-base">{r.amt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── EARNING POTENTIAL (Per Month) ───────────────────────── */}
        <section data-testid="opp-earning">
          <SectionHead
            eyebrow="Earning Potential (Per Month)"
            title="What You Can Earn at Every Level"
            sub="Realistic monthly earning bands based on average business per role."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {EARNING_POTENTIAL.map((r, i) => (
              <div
                key={r.level}
                data-testid={`opp-earn-${i}`}
                className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-5"
              >
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">{r.level}</div>
                <div className="mt-2 text-xs text-white/55">Average business · {r.biz}</div>
                <div className="mt-3 font-display text-2xl text-[#F0D788]">{r.total}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── DSA BENEFITS + MONTHLY EXAMPLE ──────────────────────── */}
        <section data-testid="opp-dsa-block" className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#D4AF37]/30 bg-white/[0.03] p-7">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
              · DSA Benefits ·
            </div>
            <h3 className="mt-2 font-display text-2xl">Seven Reasons DSAs Win With Sahyog</h3>
            <ul className="mt-5 space-y-3 text-sm text-white/85">
              {DSA_BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle2 size={15} className="text-emerald-300 mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 p-7">
            <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300">
              · Monthly Achievement Example (For DSA) ·
            </div>
            <h3 className="mt-2 font-display text-2xl">A real DSA payslip — broken down</h3>
            <dl className="mt-5 divide-y divide-white/10">
              {[
                ["Loan Disbursed", "₹25,00,000"],
                ["Commission Rate", "0.50%"],
                ["Your Earning", "₹12,500"],
                ["+ Incentives", "₹5,000"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-3 text-sm">
                  <dt className="text-white/70">{k}</dt>
                  <dd className="text-white font-medium">{v}</dd>
                </div>
              ))}
              <div className="flex items-center justify-between py-4">
                <dt className="text-[11px] tracking-[0.22em] uppercase text-emerald-300">Total Earning</dt>
                <dd className="font-display text-3xl text-[#F0D788]">₹17,500</dd>
              </div>
            </dl>
            <div className="mt-3 text-[11px] text-white/55">
              Per month · Assumes one ₹25 L disbursement. Scale this up with team building & higher slabs.
            </div>
          </div>
        </section>



        {/* ── PREMIUM SERVICES ─────────────────────────────────────── */}
        <section data-testid="opp-services">
          <SectionHead eyebrow="Our Premium Services" title="Nine Pillars of Expert Advisory" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PREMIUM_SERVICES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`opp-service-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl border border-[#D4AF37]/25 bg-white/5 p-6 hover:border-[#D4AF37]/60 hover:bg-white/[0.07] transition"
              >
                <Icon className="text-[#D4AF37]" size={30} />
                <div className="mt-4 font-display text-xl">{title}</div>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── WHY CHOOSE US ───────────────────────────────────────── */}
        <section data-testid="opp-why-choose">
          <SectionHead eyebrow="Why Choose Us?" title="Six Reasons Partners Trust Us" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_CHOOSE.map((w) => (
              <div
                key={w}
                data-testid={`opp-why-${w.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4"
              >
                <CheckCircle2 className="text-emerald-300 shrink-0" size={20} />
                <span className="text-sm sm:text-base">{w}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── INCOME CATEGORIES ────────────────────────────────────── */}
        <section data-testid="opp-income-categories">
          <SectionHead
            eyebrow="Multiple Income Categories"
            title="Ten Ways to Earn With Sahyog"
          />
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {INCOME_CATEGORIES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                data-testid={`opp-income-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl border border-[#D4AF37]/30 bg-[#0A1F44] p-5 text-center hover:scale-[1.03] transition"
              >
                <Icon className="text-[#D4AF37] mx-auto" size={26} />
                <div className="mt-3 text-[11px] tracking-[0.12em] uppercase text-white/85 break-words">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── ADVISOR COMMISSION + INCOME STRUCTURE ────────────────── */}
        <section data-testid="opp-advisor-block" className="grid lg:grid-cols-2 gap-6">
          {/* Advisor Commission card */}
          <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-7">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
              · Advisor Commission ·
            </div>
            <div className="mt-2 text-sm text-white/70">Financial Advisor</div>
            <div className="font-display text-7xl text-[#D4AF37] leading-none mt-3">
              1.50<span className="text-4xl">%</span>
            </div>
            <ul className="mt-6 space-y-2.5 text-sm text-white/85">
              {ADVISOR_PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CheckCircle2 size={15} className="text-emerald-300 mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Income Structure */}
          <div className="rounded-2xl border border-[#D4AF37]/30 bg-white/5 p-7">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
              · Advisor Income Structure ·
            </div>
            <h3 className="mt-2 font-display text-2xl">Commission by Role</h3>
            <div className="mt-5 divide-y divide-white/10 rounded-xl overflow-hidden border border-white/10">
              <div className="grid grid-cols-2 bg-[#0A1F44] text-[11px] tracking-[0.2em] uppercase px-5 py-3 text-white/70">
                <span>Level</span>
                <span>Commission</span>
              </div>
              {INCOME_STRUCTURE.map((row, i) => (
                <div
                  key={row.level}
                  data-testid={`opp-incstruct-${i}`}
                  className="grid grid-cols-2 items-center px-5 py-4"
                >
                  <span className="text-sm sm:text-base">{row.level}</span>
                  <span className="font-display text-[#F0D788] text-lg">{row.commission}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY PARTNER WITH US ──────────────────────────────────── */}
        <section data-testid="opp-why-partner">
          <SectionHead
            eyebrow="Why Partner With Us?"
            title="Ten Compounding Benefits of the Sahyog Partnership"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {WHY_PARTNER.map(({ icon: Icon, label }) => (
              <div
                key={label}
                data-testid={`opp-whypartner-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-xl border border-[#D4AF37]/25 bg-[#0A1F44]/70 p-4 text-center"
              >
                <Icon className="text-[#F0D788] mx-auto" size={22} />
                <div className="mt-2 text-[10px] tracking-[0.16em] uppercase text-white/80">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SLAB STRUCTURE TABLE ─────────────────────────────────── */}
        <section data-testid="opp-slabs">
          <SectionHead
            eyebrow="Slab Level & Benefits Structure"
            title="Five Career Slabs · Fixed Salary + Commission + Benefits"
          />
          <div className="overflow-x-auto rounded-2xl border border-[#D4AF37]/30">
            <table className="min-w-full text-sm">
              <thead className="bg-[#0A1F44] text-[11px] tracking-[0.2em] uppercase text-white/80">
                <tr>
                  <th className="px-4 py-3 text-left">Slab</th>
                  <th className="px-4 py-3 text-left">Monthly Disbursement</th>
                  <th className="px-4 py-3 text-left">Commission</th>
                  <th className="px-4 py-3 text-left">Fixed Salary</th>
                  <th className="px-4 py-3 text-left">Benefits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {SLABS.map((s, i) => (
                  <tr
                    key={s.slab}
                    data-testid={`opp-slab-row-${i}`}
                    className={i % 2 === 0 ? "bg-white/[0.02]" : "bg-transparent"}
                  >
                    <td className="px-4 py-4 align-top">
                      <div className="font-display text-[#F0D788] text-lg">{s.slab}</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-white/55">
                        {s.level}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top whitespace-nowrap">{s.range}</td>
                    <td className="px-4 py-4 align-top">
                      <span className="font-display text-2xl text-[#D4AF37]">{s.commission}</span>
                    </td>
                    <td className="px-4 py-4 align-top">{s.salary}</td>
                    <td className="px-4 py-4 align-top text-white/80">
                      <ul className="space-y-1">
                        {s.benefits.map((b) => (
                          <li key={b} className="flex items-start gap-1.5">
                            <CheckCircle2 size={13} className="text-emerald-300 mt-0.5 shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── BECOME A DSA · ELIGIBILITY ───────────────────────────── */}
        <section data-testid="opp-become-dsa" className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/20 via-[#0A1F44] to-[#04102B] p-8 text-center">
            <Medal className="text-[#D4AF37] mx-auto" size={36} />
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] mt-4">
              · Become a ·
            </div>
            <h3 className="font-display text-6xl text-[#F0D788] mt-2 tracking-tight">DSA</h3>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/70 mt-2">
              Direct Selling Agent
            </div>
            <p className="mt-5 font-display text-xl text-white">
              Build Your Dream Income With Us!
            </p>
            <Link
              data-testid="opp-dsa-apply"
              to="/apply/dsa"
              className="mt-7 inline-flex items-center gap-2 px-7 py-3 rounded-full btn-gold font-semibold"
            >
              Apply Now <ArrowRight size={16} />
            </Link>
          </div>

          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-7">
            <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300">
              · Eligibility ·
            </div>
            <h3 className="mt-2 font-display text-2xl">Who Can Join</h3>
            <div className="mt-5 divide-y divide-white/10">
              {ELIGIBILITY.map((e) => (
                <div
                  key={e.label}
                  data-testid={`opp-eligibility-${e.label.toLowerCase()}`}
                  className="flex items-center justify-between py-3 text-sm sm:text-base"
                >
                  <span className="text-white/65">{e.label}</span>
                  <span className="text-white font-medium text-right">{e.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── OUR FINANCIAL SERVICES ───────────────────────────────── */}
        <section data-testid="opp-financial-services">
          <SectionHead
            eyebrow="Our Financial Services"
            title="Fourteen Loan &amp; Finance Products Under One Roof"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
            {FINANCIAL_SERVICES.map((f) => (
              <Link
                key={f}
                to="/apply/loan"
                data-testid={`opp-fs-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-between gap-3 px-5 py-4 rounded-xl border border-[#D4AF37]/30 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#D4AF37]/60 transition"
              >
                <span className="text-sm sm:text-base">{f}</span>
                <ArrowRight size={14} className="text-[#D4AF37]" />
              </Link>
            ))}
          </div>
          <div className="mt-4 text-center text-xs uppercase tracking-[0.28em] text-[#D4AF37]">
            ROI 6.5% – 15% p.a. · Quick Approval
          </div>
        </section>

        {/* ── SUPPORT / BENEFITS STRIP ─────────────────────────────── */}
        <section
          data-testid="opp-support-strip"
          className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        >
          {SUPPORT_BENEFITS.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="rounded-xl border border-[#D4AF37]/25 bg-[#0A1F44]/70 px-4 py-5 text-center"
            >
              <Icon className="text-[#F0D788] mx-auto" size={22} />
              <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-white/80">
                {label}
              </div>
            </div>
          ))}
        </section>

        {/* ── WHO CAN JOIN ─────────────────────────────────────────── */}
        <section data-testid="opp-who-can-join">
          <SectionHead
            eyebrow="Who Can Join?"
            title="Seven Profiles · One Career Path"
            sub="Whether you run a shop, sell insurance, or are a student — there's a DSA role with your name on it."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {WHO_CAN_JOIN.map((w) => (
              <div
                key={w}
                data-testid={`opp-who-${w.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="flex items-center gap-3 rounded-xl border border-[#D4AF37]/30 bg-white/[0.03] px-5 py-4 hover:bg-white/[0.06] transition"
              >
                <CheckCircle2 className="text-[#F0D788] shrink-0" size={18} />
                <span className="text-sm sm:text-base">{w}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── WHY JOIN AS DSA (BILINGUAL) ──────────────────────────── */}
        <section data-testid="opp-why-join">
          <SectionHead
            eyebrow="Why Join As DSA?"
            title="Eight Reasons · Bilingual Clarity"
            sub="No investment · no fixed income required. Your effort is your earning."
          />
          <div className="grid md:grid-cols-2 gap-3">
            {WHY_JOIN.map((w) => (
              <div
                key={w.en}
                data-testid={`opp-whyjoin-${w.en.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="flex items-start gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5 px-5 py-4"
              >
                <CheckCircle2 className="text-emerald-300 mt-0.5 shrink-0" size={18} />
                <div>
                  <div className="text-sm sm:text-base font-medium">{w.en}</div>
                  <div className="text-xs sm:text-sm text-emerald-200/85 font-display mt-0.5">
                    {w.gu}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center font-display text-2xl text-[#F0D788]">
            Become DSA &amp; Build Your Dream Income!
          </div>
        </section>

        {/* ── DSA WORK PROCESS ─────────────────────────────────────── */}
        <section data-testid="opp-dsa-process">
          <SectionHead
            eyebrow="DSA Work Process"
            title="From Sign-Up to Payout — In Six Steps"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DSA_PROCESS.map((p) => (
              <div
                key={p.step}
                data-testid={`opp-process-${p.step}`}
                className="relative rounded-2xl border border-[#D4AF37]/30 bg-[#0A1F44]/70 p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-5xl text-white/20 leading-none">
                    {p.step}
                  </span>
                  <p.icon className="text-[#F0D788]" size={26} />
                </div>
                <div className="mt-4 font-medium text-base">{p.title}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── WHY CHOOSE SAHYOG (COMPARISON) ───────────────────────── */}
        <section data-testid="opp-comparison">
          <SectionHead
            eyebrow="Why Choose Sahyog?"
            title="The Sahyog Advantage vs. The Industry Standard"
            sub="શા માટે SAHYOG પસંદ કરો? — Same loans, fewer barriers."
          />
          <div className="space-y-4">
            {COMPARISON.map((row, i) => (
              <div
                key={`compare-${i}-${(row.them || "").slice(0, 24)}`}
                data-testid={`opp-compare-${i}`}
                className="grid md:grid-cols-2 gap-3"
              >
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-5">
                  <div className="flex items-center gap-2 text-rose-300 text-[10px] tracking-[0.22em] uppercase">
                    <XCircle size={14} /> Industry Standard
                  </div>
                  <div className="mt-2 text-sm sm:text-base">{row.them}</div>
                  <div className="mt-1 text-xs text-rose-200/80 font-display">
                    {row.themGu}
                  </div>
                </div>
                <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5">
                  <div className="flex items-center gap-2 text-emerald-300 text-[10px] tracking-[0.22em] uppercase">
                    <CheckCircle2 size={14} /> The Sahyog Way
                  </div>
                  <div className="mt-2 text-sm sm:text-base">{row.us}</div>
                  <div className="mt-1 text-xs text-emerald-200/85 font-display">
                    {row.usGu}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── REQUIRED DOCUMENTS ───────────────────────────────────── */}
        <section data-testid="opp-documents">
          <SectionHead
            eyebrow="Required Documents"
            title="Six Documents · Easy & Minimal"
            sub="જરૂરી દસ્તાવેજો — only what's strictly needed for compliance."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {DOCUMENTS.map((d) => (
              <div
                key={d.en}
                data-testid={`opp-doc-${d.en.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="flex items-start gap-3 rounded-xl border border-[#D4AF37]/25 bg-white/[0.03] px-5 py-4"
              >
                <FileText className="text-[#F0D788] mt-0.5 shrink-0" size={18} />
                <div>
                  <div className="text-sm sm:text-base font-medium">{d.en}</div>
                  <div className="text-xs text-white/55 font-display mt-0.5">{d.gu}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── BUSINESS VERTICALS ───────────────────────────────────── */}
        <section data-testid="opp-verticals">
          <SectionHead
            eyebrow="Our Business Verticals"
            title="Three Pillars · One Trusted Brand"
          />
          <div className="grid md:grid-cols-3 gap-5">
            {BUSINESS_VERTICALS.map((v) => (
              <div
                key={v.title}
                data-testid={`opp-vertical-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-2xl border bg-gradient-to-br ${v.accent} to-transparent p-6`}
              >
                <v.icon className="text-[#F0D788]" size={30} />
                <div className="mt-4 font-display text-2xl">{v.title}</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mt-1">
                  {v.tag}
                </div>
                <p className="mt-3 text-sm text-white/75 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── 8 PARTNERSHIP PERKS ──────────────────────────────────── */}
        <section data-testid="opp-perks">
          <SectionHead
            eyebrow="Partnership Perks"
            title="Eight Built-in Advantages"
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PARTNERSHIP_PERKS.map(({ icon: Icon, label }) => (
              <div
                key={label}
                data-testid={`opp-perk-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="rounded-xl border border-[#D4AF37]/25 bg-[#0A1F44]/70 p-4 text-center"
              >
                <Icon className="text-[#F0D788] mx-auto" size={22} />
                <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-white/85">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── MOTTO / QUOTE ────────────────────────────────────────── */}
        <section
          data-testid="opp-motto"
          className="rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-8 md:p-14"
        >
          <Quote className="text-[#D4AF37]" size={36} />
          <p className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl leading-snug max-w-4xl">
            દરેક મોટું સપનું એક નાનકડા નિર્ણયથી શરૂ થાય છે. આજે લીધેલું એક યોગ્ય પગલું
            આવતીકાલની મોટી સફળતાનું કારણ બની શકે છે.
          </p>
          <p className="mt-3 text-white/65 text-sm max-w-3xl">
            Every great dream begins with one small decision. The right step taken today can
            be the reason for tomorrow&apos;s biggest success.
          </p>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {MOTTO_LINES.map((m) => (
              <div
                key={m.en}
                className="rounded-xl border border-[#D4AF37]/30 bg-white/[0.03] p-5"
              >
                <div className="font-display text-lg text-[#F0D788]">{m.en}</div>
                <div className="text-xs text-white/60 font-display mt-1">{m.gu}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center font-display text-xl text-[#D4AF37]">
            તમારી મહેનત તમારી સૌથી મોટી મૂડી છે.
          </div>
        </section>

        {/* ── TRUST PILLARS ────────────────────────────────────────── */}
        <section
          data-testid="opp-trust"
          className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        >
          {TRUST_PILLARS.map(({ icon: Icon, label }) => (
            <div
              key={label}
              data-testid={`opp-trust-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 px-4 py-5 text-center"
            >
              <Icon className="text-emerald-300 mx-auto" size={22} />
              <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-white/85">
                {label}
              </div>
            </div>
          ))}
        </section>

        {/* ── REGISTRATIONS / COMPLIANCE ───────────────────────────── */}
        <section data-testid="opp-registrations">
          <SectionHead
            eyebrow="Compliance & Trust"
            title="Five Government Registrations · Fully Verified"
            sub="Sahyog Finance &amp; Noble Infinity Asset Management Pvt. Ltd. is a registered private limited company with all statutory approvals in place."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {REGISTRATIONS.map((r) => (
              <div
                key={r.label}
                data-testid={`opp-reg-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="rounded-xl border border-[#D4AF37]/30 bg-[#0A1F44]/70 p-4 text-center"
              >
                <r.icon className="text-[#F0D788] mx-auto" size={22} />
                <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">
                  {r.label}
                </div>
                <div className="mt-1 font-mono text-[11px] text-white/85 break-all">
                  {r.value}
                </div>
              </div>
            ))}
          </div>
        </section>



        {/* ── CONTACT CTA ──────────────────────────────────────────── */}
        <section
          data-testid="opp-contact"
          className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-8 md:p-12"
        >
          <div className="grid md:grid-cols-1 gap-6 items-center">
            <div>
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37]">
                · Connect Now ·
              </div>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl">
                Ready to start? Call or WhatsApp our team.
              </h3>
              <div className="mt-5 flex flex-wrap gap-3 text-white/85">
                <a
                  href="tel:+917016975324"
                  data-testid="opp-call-1"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-outline-gold text-sm"
                >
                  <Phone size={14} /> 70169 75324
                </a>
                <a
                  href="tel:+919879630926"
                  data-testid="opp-call-2"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-outline-gold text-sm"
                >
                  <Phone size={14} /> 98796 30926
                </a>
                <a
                  href="https://wa.me/917016975324"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="opp-whatsapp-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 text-sm hover:bg-emerald-600/30"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a
                  href="mailto:sahyogpfir@gmail.com"
                  data-testid="opp-email-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-outline-gold text-sm"
                >
                  <Mail size={14} /> sahyogpfir@gmail.com
                </a>
              </div>
              <div className="mt-6 flex items-start gap-2 text-sm text-white/70">
                <MapPin size={16} className="text-[#D4AF37] mt-0.5" />
                <span>
                  Registered Office: A-106, Ambaji Complex, Unai Road, Vyara, Tapi, Gujarat – 394650
                </span>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* ─── helper components ──────────────────────────────────────────────── */

function SectionHead({ eyebrow, title, sub }) {
  return (
    <div className="text-center mb-10">
      <span className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37]">
        · {eyebrow} ·
      </span>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight tracking-tight">
        {title}
      </h2>
      {sub && (
        <p className="mt-4 max-w-2xl mx-auto text-white/70 text-sm sm:text-base">{sub}</p>
      )}
    </div>
  );
}

function WhatsAppRolePicker() {
  const [role, setRole] = useState("advisor");
  const [name, setName] = useState("");

  const ROLES = [
    { value: "advisor",       label: "Become Advisor" },
    { value: "dsa",           label: "Become DSA" },
    { value: "ap",            label: "Associate Partner" },
    { value: "agency",        label: "Become Agency Partner" },
    { value: "sub_branch",    label: "Sub-branch" },
    { value: "branch",        label: "Branch" },
  ];

  const roleLabel = ROLES.find((r) => r.value === role)?.label || role;
  const message = encodeURIComponent(
    `Hello Sahyog Finance, I'm ${name.trim() || "[your name]"} and I'd like to register as: ${roleLabel}. Please share the next steps.`
  );
  const href = `https://wa.me/917016975324?text=${message}`;

  return (
    <div
      data-testid="opp-whatsapp-picker"
      className="mt-8 max-w-3xl mx-auto rounded-2xl border border-emerald-500/40 bg-emerald-500/[0.07] p-5 md:p-6 text-left"
    >
      <div className="flex items-center gap-2 text-emerald-200 text-[11px] uppercase tracking-[0.24em]">
        <MessageCircle size={14} /> Register via WhatsApp
      </div>
      <div className="mt-3 grid md:grid-cols-[1fr,1fr,auto] gap-3 items-end">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-white/60 mb-1.5 block">
            Your name
          </span>
          <input
            data-testid="opp-wa-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Ravi Patel"
            className="w-full px-4 py-2.5 rounded-lg bg-white/[0.06] border border-emerald-500/30 outline-none focus:border-emerald-400/70 text-sm text-white placeholder:text-white/30"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-white/60 mb-1.5 block">
            I want to become
          </span>
          <select
            data-testid="opp-wa-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg bg-white/[0.06] border border-emerald-500/30 outline-none focus:border-emerald-400/70 text-sm text-white"
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <a
          data-testid="opp-wa-submit"
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#04102B] text-sm font-semibold whitespace-nowrap transition"
        >
          <MessageCircle size={15} /> Register on WhatsApp
        </a>
      </div>
      <p className="mt-3 text-[11px] text-white/50">
        Opens a pre-filled WhatsApp chat with our onboarding desk — no forms, no waiting.
      </p>
    </div>
  );
}
